// Types extracted from project-details.tsx

import type { ColumnDefinition } from '@/components/column-configurator-drawer';

export interface ProjectDetailsPageProps {
  projectId?: string;
  onBack?: () => void;
}

export interface InternalTabConfig {
  id: string;
  label: string;
  IconComponent: React.ComponentType<{ className?: string }>;
}

export interface TeamMember {
  id: number | string;
  employeeId: number;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  allocatedHours: number;
  actualHours: number;
  remaining: number;
  startDate: string;
  endDate: string;
  status: "On Track" | "At Risk" | "Over Allocated";
  utilisationPercent: number;
}

export type BurnPoint = {
  month: string;
  planned: number;
  eac: number | null;
  budget: number;
  isToday: boolean;
};

export type HoursBurndownPoint = {
  month: string;
  planned: number | null;
  eac: number | null;
  /** HTC-based EAC line — only populated when devHtcAvailable is true */
  eacHtc?: number | null;
  isToday: boolean;
};

export const DEFAULT_INVOICE_COLUMNS: ColumnDefinition[] = [
  {
    id: 'invoice-number',
    name: 'Invoice #',
    description: 'Invoice number',
    category: 'Status',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'date',
    name: 'Date',
    description: 'Invoice date',
    category: 'Timeline',
    visible: true,
    order: 1,
  },
  {
    id: 'description',
    name: 'Description',
    description: 'Invoice description',
    category: 'Status',
    visible: true,
    order: 2,
  },
  {
    id: 'net',
    name: 'Net',
    description: 'Invoice amount excluding VAT',
    category: 'Financial',
    visible: true,
    order: 3,
    align: 'right' as const,
  },
  {
    id: 'vat',
    name: 'VAT',
    description: 'VAT amount',
    category: 'Financial',
    visible: true,
    order: 4,
    align: 'right' as const,
  },
  {
    id: 'amount',
    name: 'Amount',
    description: 'Invoice amount including VAT',
    category: 'Financial',
    visible: true,
    order: 5,
    align: 'right' as const,
  },
  {
    id: 'days-overdue',
    name: 'Days Overdue',
    description: 'Number of days past due date',
    category: 'Status',
    visible: true,
    order: 6,
    align: 'right' as const,
  },
  {
    id: 'status',
    name: 'Status',
    description: 'Invoice status',
    category: 'Status',
    visible: true,
    order: 7,
  },
];

// Default WIP column definitions (extracted for useColumnPreferences)
export const DEFAULT_WIP_COLUMNS: ColumnDefinition[] = [
  {
    id: 'date',
    name: 'Date',
    description: 'Work date',
    category: 'Timeline',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'resource',
    name: 'Resource',
    description: 'Person or resource',
    category: 'Resource',
    visible: true,
    order: 1,
  },
  {
    id: 'task',
    name: 'Task',
    description: 'Task name',
    category: 'Status',
    visible: false,
    order: 2,
    hidden: true,
  },
  {
    id: 'description',
    name: 'Description',
    description: 'Work description',
    category: 'Status',
    visible: true,
    order: 3,
  },
  {
    id: 'type',
    name: 'Type',
    description: 'Time or Expense',
    category: 'Status',
    visible: true,
    order: 4,
  },
  {
    id: 'quantity',
    name: 'Qty',
    description: 'Quantity (hours or items)',
    category: 'Financial',
    visible: true,
    order: 5,
    align: 'right' as const,
  },
  {
    id: 'rate',
    name: 'Rate',
    description: 'Billing rate',
    category: 'Financial',
    visible: true,
    order: 6,
    align: 'right' as const,
  },
  {
    id: 'amount',
    name: 'Amount',
    description: 'Total amount',
    category: 'Financial',
    visible: true,
    order: 7,
    align: 'right' as const,
  },
  {
    id: 'costCentre',
    name: 'Cost Centre',
    description: 'External cost centre code',
    category: 'Financial',
    visible: true,
    order: 8,
  },
];
